import React, { useState, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

export default function IncomeShiftReport() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();

  // Get current year and month for default filter
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // Placeholder data - will be replaced with real data
  const [data] = useState([
    {
      id: 1,
      shiftDate: '2024-11-22',
      driverName: 'Erik Hansen',
      vehicleNumber: 'EL 12345',
      startTime: '06:00',
      endTime: '14:00',
      totalHours: 8,
      totalTrips: 15,
      grossIncome: 4850,
      netIncome: 3880,
      status: 'completed',
    },
    {
      id: 2,
      shiftDate: '2024-11-22',
      driverName: 'Anna Olsen',
      vehicleNumber: 'EK 67890',
      startTime: '14:00',
      endTime: '22:00',
      totalHours: 8,
      totalTrips: 18,
      grossIncome: 5620,
      netIncome: 4496,
      status: 'completed',
    },
    {
      id: 3,
      shiftDate: '2024-11-21',
      driverName: 'Lars Berg',
      vehicleNumber: 'EB 11223',
      startTime: '22:00',
      endTime: '06:00',
      totalHours: 8,
      totalTrips: 12,
      grossIncome: 4200,
      netIncome: 3360,
      status: 'completed',
    },
    {
      id: 4,
      shiftDate: '2024-11-21',
      driverName: 'Kari Nilsen',
      vehicleNumber: 'EV 44556',
      startTime: '08:00',
      endTime: '16:00',
      totalHours: 8,
      totalTrips: 14,
      grossIncome: 4100,
      netIncome: 3280,
      status: 'pending',
    },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [monthFilter, setMonthFilter] = useState(currentMonth);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);

  const filteredData = useMemo(() => {
    let result = data;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.driverName && d.driverName.toLowerCase().includes(query)) ||
        (d.vehicleNumber && d.vehicleNumber.toLowerCase().includes(query))
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(d => d.status === statusFilter);
    }

    return result;
  }, [data, searchQuery, statusFilter]);

  // Summary stats
  const summaryData = useMemo(() => ({
    totalShifts: data.length,
    totalHours: data.reduce((sum, d) => sum + (d.totalHours || 0), 0),
    totalTrips: data.reduce((sum, d) => sum + (d.totalTrips || 0), 0),
    totalGrossIncome: data.reduce((sum, d) => sum + (d.grossIncome || 0), 0),
    totalNetIncome: data.reduce((sum, d) => sum + (d.netIncome || 0), 0),
  }), [data]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      completed: { label: t('completed') || 'Completed', bgcolor: '#DCFCE7', color: '#16A34A' },
      pending: { label: t('pending') || 'Pending', bgcolor: '#FEF3C7', color: '#D97706' },
      cancelled: { label: t('cancelled') || 'Cancelled', bgcolor: '#FEE2E2', color: '#DC2626' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          fontWeight: 600,
          fontSize: 12,
        }}
      />
    );
  };

  const columns = [
    {
      title: t('shift_date') || 'Date',
      field: 'shiftDate',
      render: rowData => (
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>
          {rowData.shiftDate || '-'}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('driver') || 'Driver',
      field: 'driverName',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PersonIcon sx={{ color: '#6366F1', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.driverName || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {rowData.vehicleNumber}
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('shift_time') || 'Shift Time',
      field: 'startTime',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: '#6B7280' }} />
          <Typography sx={{ fontSize: 13, color: '#374151' }}>
            {rowData.startTime} - {rowData.endTime}
          </Typography>
          <Chip
            label={`${rowData.totalHours}h`}
            size="small"
            sx={{ ml: 1, bgcolor: '#F3F4F6', color: '#374151', fontSize: 11, height: 20 }}
          />
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('trips') || 'Trips',
      field: 'totalTrips',
      render: rowData => (
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
          {rowData.totalTrips || 0}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px', textAlign: 'center' },
    },
    {
      title: t('gross_income') || 'Gross',
      field: 'grossIncome',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {formatCurrency(rowData.grossIncome || 0)}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('net_income') || 'Net',
      field: 'netIncome',
      render: rowData => (
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#16A34A' }}>
          {formatCurrency(rowData.netIncome || 0)}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('status') || 'Status',
      field: 'status',
      render: rowData => getStatusChip(rowData.status),
      cellStyle: { padding: '14px 16px' },
    },
  ];

  // Year options (last 5 years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Month options
  const monthOptions = [
    { value: 1, label: t('january') || 'January' },
    { value: 2, label: t('february') || 'February' },
    { value: 3, label: t('march') || 'March' },
    { value: 4, label: t('april') || 'April' },
    { value: 5, label: t('may') || 'May' },
    { value: 6, label: t('june') || 'June' },
    { value: 7, label: t('july') || 'July' },
    { value: 8, label: t('august') || 'August' },
    { value: 9, label: t('september') || 'September' },
    { value: 10, label: t('october') || 'October' },
    { value: 11, label: t('november') || 'November' },
    { value: 12, label: t('december') || 'December' },
  ];

  return (
    <Box style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('income_shift_report') || 'Income/Shift Report'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('income_shift_report_description') || 'View shift data and income reports by driver and vehicle'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          sx={{
            borderColor: '#E5E7EB',
            color: '#374151',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' },
          }}
        >
          {t('export') || 'Export'}
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={2.4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptLongIcon sx={{ color: '#6366F1', fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>
                {t('shifts') || 'Shifts'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalShifts}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccessTimeIcon sx={{ color: '#2563EB', fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>
                {t('total_hours') || 'Hours'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalHours}h
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#D97706', fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>
                {t('trips') || 'Trips'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalTrips}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AttachMoneyIcon sx={{ color: '#9333EA', fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>
                {t('gross') || 'Gross'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
              {formatCurrency(summaryData.totalGrossIncome)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AttachMoneyIcon sx={{ color: '#16A34A', fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>
                {t('net') || 'Net'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#16A34A' }}>
              {formatCurrency(summaryData.totalNetIncome)}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Filter Toolbar */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F9FAFB',
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            {yearOptions.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            {monthOptions.map(month => (
              <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            <MenuItem value="all">{t('all_status') || 'All Status'}</MenuItem>
            <MenuItem value="completed">{t('completed') || 'Completed'}</MenuItem>
            <MenuItem value="pending">{t('pending') || 'Pending'}</MenuItem>
            <MenuItem value="cancelled">{t('cancelled') || 'Cancelled'}</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 13, color: '#6B7280', ml: 'auto' }}>
          <strong style={{ color: '#111827' }}>{filteredData.length}</strong> {t('results') || 'results'}
        </Typography>
      </Box>

      {/* Table */}
      <MaterialTable
        icons={TableIcons}
        title={null}
        columns={columns}
        data={filteredData}
        onRowClick={(evt, selectedRow) => setSelectedRow(selectedRow.tableData.id)}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: 12,
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
        options={{
          ...tableOptions,
          search: false,
          toolbar: false,
          showTitle: false,
          rowStyle: rowData => ({
            backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
          }),
        }}
        localization={{
          body: { emptyDataSourceMessage: t('no_data') || 'No data to display' },
          pagination: {
            labelDisplayedRows: '{from}-{to} ' + (t('of') || 'of') + ' {count}',
            firstTooltip: t('first_page_tooltip'),
            previousTooltip: t('previous_page_tooltip'),
            nextTooltip: t('next_page_tooltip'),
            lastTooltip: t('last_page_tooltip'),
          },
        }}
      />
    </Box>
  );
}
